import { ArtificialPlayer } from '../../ArtificialPlayers/ArtificialPlayer';
import { DEFAULT_GAME_PLAYER } from '../GameDefaults';
import GamePlayerAPI from './GamePlayerAPI';


// Single sender debounce window for rotate emits. Layout reflows can
// fire onSlideChange in tight bursts; debouncing collapses the burst
// into one wire Rotate carrying the final position.
const ROTATE_DEBOUNCE_MS = 100;
const DEFAULT_ACTION_HEX_POSITION = 0;  // Research, per Manual §7.1.


export default class GamePlayerController {

    constructor(game, seat) {
        this.game = game;
        this.gameData = game.gameData;
        this.seat = seat;
        this.name = `GamePlayerController-${this.seat}`;
        this.eventName = `game-player-${this.seat}-event`;

        this.gamePlayerData = { ...DEFAULT_GAME_PLAYER };
        this.gamePlayerData.seat = seat;

        // Action-chain head last seen for this seat (CC_ACTION_CHAIN_UUID).
        // Set from every incoming GamePlayer patch; echoed by
        // GamePlayerPackageSender as parent_action_uuid on each outgoing
        // click. Null until the first patch arrives.
        this.lastActionUuid = null;

        // Local intent for this seat's action hex. Once seeded, server
        // echoes of action_hex_position for this seat are ignored —
        // local input is the source of truth for the hex's rendered
        // position. See action-hex-event handling below.
        this.localIntentPosition = null;
        this._localIntentSeeded = false;
        this._rotateTimer = null;

        this.gamePlayerApi = new GamePlayerAPI(this);
        this.artificialPlayer = new ArtificialPlayer(this);

        window.addEventListener('game-player-event',
            this.gamePlayerEventListener);

        window.addEventListener('action-hex-event',
            this.actionHexEventListener);
    }

    patchGamePlayer = (field, value) => {
        this.gamePlayerData[field] = value;
        window.dispatchEvent(
            new CustomEvent(this.eventName, { detail: this.gamePlayerData }));
    }

    isLocalSeat = () => {
        return this.game && this.game.currentSeat === this.seat;
    }

    updateGamePlayerData(gamePlayerData) {
        // Capture the action-chain head from every incoming patch,
        // independent of the shouldUpdate diff gate below — the head
        // rotates on the backend with each broadcast and must always
        // ride forward so the next click echoes the latest value.
        if (gamePlayerData.last_action_uuid !== undefined
                && gamePlayerData.last_action_uuid !== null) {
            this.lastActionUuid = gamePlayerData.last_action_uuid;
        }
        // Preserve local intent for own seat: incoming wire updates
        // never overwrite the locally-owned action_hex_position while
        // the hex is unlocked. Lock-state transitions and explicit
        // engine writes via lockTheActionHex (committed position) flow
        // through unchanged.
        if (this.isLocalSeat() && this._localIntentSeeded
                && !gamePlayerData.action_hex_locked) {
            if (gamePlayerData.action_hex_position !== this.localIntentPosition) {
                gamePlayerData = {
                    ...gamePlayerData,
                    action_hex_position: this.localIntentPosition,
                };
            }
        } else if (this.isLocalSeat() && !this._localIntentSeeded) {
            const seeded = gamePlayerData.action_hex_position;
            const initialPosition =
                (seeded === null || seeded === undefined)
                    ? DEFAULT_ACTION_HEX_POSITION : seeded;
            this.localIntentPosition = initialPosition;
            this._localIntentSeeded = true;
            if (seeded !== initialPosition) {
                gamePlayerData = {
                    ...gamePlayerData,
                    action_hex_position: initialPosition,
                };
            }
        }
        if (this.shouldUpdate(gamePlayerData)) {
            // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: the send-layer duplicate
            // latch (GamePlayerPackageSender) suppresses a byte-identical
            // re-send only WITHIN one continuous decision state. The moment
            // the seat's decision lifecycle (status/turn_status/action_status)
            // moves — including a transient bounce OUT of and back INTO a
            // click state, e.g. GHOSTED -> WAITING -> GHOSTED while placing
            // several received ghosts — the next click is a genuinely NEW
            // decision even when its payload is identical (both ghosts weight
            // to the same dock). Clear the latch here, on the patch that moves
            // the lifecycle, so the re-entry is never mistaken for the
            // in-flight duplicate the latch is meant to drop.
            if (this._decisionLifecycle(this.gamePlayerData)
                    !== this._decisionLifecycle(gamePlayerData)) {
                this._lastClickFingerprint = null;
                this._lastClickLifecycle = null;
            }
            console.log(this.name, 'updateGamePlayerData');
            this.gamePlayerData = gamePlayerData;
            window.dispatchEvent(
                new CustomEvent(this.eventName, { detail: this.gamePlayerData }));
        }
    }

    // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: the seat's decision lifecycle — the
    // triple whose change marks a genuinely new decision. Mirrors the key the
    // package sender's duplicate latch is scoped to.
    _decisionLifecycle = (data) => {
        return `${data.status}|${data.turn_status}|${data.action_status}`;
    }

    shouldUpdate = (gamePlayerData) => {
        return JSON.stringify(this.gamePlayerData) !== JSON.stringify(gamePlayerData);
    }

    gamePlayerEventListener = (e) => {
        this.gameData = this.game.gameData;  // stale to start, many reference by game_player.game
        const gamePlayerData = e.detail;
        if (gamePlayerData.seat === this.seat) {
            this.updateGamePlayerData(gamePlayerData);
        }
    }

    actionHexEventListener = (e) => {
        if (this.seat !== e.detail.seat) return;
        if (this.gamePlayerData.action_hex_locked) return;
        const position = e.detail.actionHexPosition;
        this.localIntentPosition = position;
        this._localIntentSeeded = true;
        this.patchGamePlayer('action_hex_position', position);
        this._scheduleRotateBroadcast(position);
    }

    _scheduleRotateBroadcast = (position) => {
        if (this._rotateTimer) clearTimeout(this._rotateTimer);
        this._rotateTimer = setTimeout(() => {
            this._rotateTimer = null;
            if (this.gamePlayerData.action_hex_locked) return;
            this.gamePlayerApi.pushActionHexPosition(position);
        }, ROTATE_DEBOUNCE_MS);
    }

    lockTheActionHex = (action_hex_position) => {
        if (this._rotateTimer) {
            clearTimeout(this._rotateTimer);
            this._rotateTimer = null;
        }
        this.localIntentPosition = action_hex_position;
        this._localIntentSeeded = true;
        this.patchGamePlayer('action_hex_position', action_hex_position);
        this.patchGamePlayer('action_hex_locked', true);
    }
}


export { ROTATE_DEBOUNCE_MS, DEFAULT_ACTION_HEX_POSITION };
